import { useState } from "react";
import type { MonthCaptionProps, PropsBase, PropsSingle } from "react-day-picker";
import { DayPicker } from "react-day-picker";

import classNames from "classnames";

import { useCustomLocale } from "./hooks/useCustomLocale/useCustomLocale";
import { CustomMonthCaption } from "./utils/CustomMonthCaption";

import { DatePickerFooter } from "./DatePickerFooter";

import "react-day-picker/style.css";
import styles from "./DatePicker.module.scss";

export type DisabledRange = { before?: Date; after?: Date };

export type DatePickerProps = Omit<PropsBase, "mode"> &
  Omit<PropsSingle, "mode"> & {
    onSaveSelection?: (selectedDate: Date | null) => void;
    currentlyBookedDate?: Date;
    footer?: React.ReactNode;
    disabledRange?: DisabledRange;
    disableDisabled?: boolean;
  };

export const DatePicker = ({
  onSaveSelection,
  currentlyBookedDate,
  numberOfMonths = 2,
  hideNavigation = true,
  disabledRange,
  footer,
  disableDisabled = false,
  ...rest
}: DatePickerProps) => {
  const customLocale = useCustomLocale();
  const [selected, setSelected] = useState<Date | undefined>(undefined);

  const handleSaveSelection = () => {
    if (onSaveSelection && selected) {
      onSaveSelection(selected);
    }
  };

  const disabledBefore = disabledRange?.before || new Date(new Date().setDate(new Date().getDate() + 1));
  const disabledAfter = disabledRange?.after || new Date(new Date().setMonth(new Date().getMonth() + 1));

  return (
    <div className={classNames(styles.wrapper, "light-theme")}>
      <DayPicker
        mode="single"
        className={styles.datePicker}
        locale={customLocale}
        numberOfMonths={numberOfMonths}
        hideNavigation={hideNavigation}
        defaultMonth={new Date()}
        selected={selected}
        onSelect={setSelected}
        useAdditionalDayOfYearTokens={true}
        components={{
          MonthCaption: (props: MonthCaptionProps) => <CustomMonthCaption {...props} />,
        }}
        {...(currentlyBookedDate
          ? {
              modifiers: { booked: currentlyBookedDate },
              modifiersClassNames: { booked: "currentlyBooked" },
            }
          : {})}
        disabled={!disableDisabled ? [{ before: disabledBefore }, { after: disabledAfter }] : []}
        {...rest}
      />
      {footer || <DatePickerFooter onSaveSelection={handleSaveSelection} selected={selected ?? null} />}
    </div>
  );
};
